import findIndex from 'array-find-index';
import React from 'react';
import warning from 'warning';
import uniqueId from 'lodash.uniqueid';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __spreadArrays() {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
}

var DOCUMENT_POSITION_PRECEDING = 2;
var ActionTypes;
(function (ActionTypes) {
    ActionTypes["REGISTER"] = "REGISTER";
    ActionTypes["UNREGISTER"] = "UNREGISTER";
    ActionTypes["TAB_TO_FIRST"] = "TAB_TO_FIRST";
    ActionTypes["TAB_TO_LAST"] = "TAB_TO_LAST";
    ActionTypes["TAB_TO_PREVIOUS"] = "TAB_TO_PREVIOUS";
    ActionTypes["TAB_TO_NEXT"] = "TAB_TO_NEXT";
    ActionTypes["CLICKED"] = "CLICKED";
    ActionTypes["CHANGE_DIRECTION"] = "CHANGE_DIRECTION";
})(ActionTypes || (ActionTypes = {}));
function reducer(state, action) {
    switch (action.type) {
        case ActionTypes.REGISTER: {
            var tabStops = state.tabStops;
            var newTabStop_1 = action.payload;
            if (tabStops.length === 0) {
                return __assign(__assign({}, state), { selectedId: newTabStop_1.id, tabStops: [newTabStop_1] });
            }
            var index = findIndex(tabStops, function (tabStop) { return tabStop.id === newTabStop_1.id; });
            if (index >= 0) {
                warning(false, newTabStop_1.id + " tab stop already registered");
                return state;
            }
            var indexToInsertAt = findIndex(tabStops, function (tabStop) {
                // Return true if newTabStop's element is located earlier in the DOM
                // than tabStop's element, else false:
                return !!(tabStop.domElementRef.current.compareDocumentPosition(newTabStop_1.domElementRef.current) & DOCUMENT_POSITION_PRECEDING);
            });
            // findIndex returns -1 when newTabStop should be inserted
            // at the end of tabStops (the compareDocumentPosition test
            // always returns false in that case).
            if (indexToInsertAt === -1) {
                indexToInsertAt = tabStops.length;
            }
            return __assign(__assign({}, state), { tabStops: __spreadArrays(tabStops.slice(0, indexToInsertAt), [
                    newTabStop_1
                ], tabStops.slice(indexToInsertAt)) });
        }
        case ActionTypes.UNREGISTER: {
            var id_1 = action.payload.id;
            var filteredTabStops = state.tabStops.filter(function (tabStop) { return tabStop.id !== id_1; });
            if (filteredTabStops.length === state.tabStops.length) {
                warning(false, id_1 + " tab stop already unregistered");
                return state;
            }
            return __assign(__assign({}, state), { selectedId: state.selectedId === id_1
                    ? filteredTabStops.length === 0
                        ? null
                        : filteredTabStops[0].id
                    : state.selectedId, tabStops: filteredTabStops });
        }
        case ActionTypes.TAB_TO_PREVIOUS:
        case ActionTypes.TAB_TO_NEXT: {
            var id_2 = action.payload.id;
            var index = findIndex(state.tabStops, function (tabStop) { return tabStop.id === id_2; });
            if (index === -1) {
                warning(false, id_2 + " tab stop not registered");
                return state;
            }
            var newIndex = action.type === ActionTypes.TAB_TO_PREVIOUS
                ? index <= 0
                    ? state.tabStops.length - 1
                    : index - 1
                : index >= state.tabStops.length - 1
                    ? 0
                    : index + 1;
            return __assign(__assign({}, state), { lastActionOrigin: "keyboard", selectedId: state.tabStops[newIndex].id });
        }
        case ActionTypes.TAB_TO_FIRST:
        case ActionTypes.TAB_TO_LAST: {
            if (!state.tabStops.length) {
                return state;
            }
            var newIndex = action.type === ActionTypes.TAB_TO_FIRST
                ? 0
                : state.tabStops.length - 1;
            return __assign(__assign({}, state), { lastActionOrigin: "keyboard", selectedId: state.tabStops[newIndex].id });
        }
        case ActionTypes.CLICKED: {
            return __assign(__assign({}, state), { lastActionOrigin: "mouse", selectedId: action.payload.id });
        }
        case ActionTypes.CHANGE_DIRECTION: {
            return __assign(__assign({}, state), { direction: action.payload.direction });
        }
        default:
            return state;
    }
}
var RovingTabIndexContext = React.createContext({
    state: {
        direction: "horizontal",
        selectedId: null,
        lastActionOrigin: null,
        tabStops: []
    },
    dispatch: function () { }
});
var Provider = function (_a) {
    var children = _a.children, _b = _a.direction, direction = _b === void 0 ? "horizontal" : _b;
    var _c = React.useReducer(reducer, {
        direction: "horizontal",
        selectedId: null,
        lastActionOrigin: null,
        tabStops: []
    }), state = _c[0], dispatch = _c[1];
    var context = React.useMemo(function () { return ({
        state: state,
        dispatch: dispatch
    }); }, [state]);
    React.useEffect(function () {
        dispatch({
            type: ActionTypes.CHANGE_DIRECTION,
            payload: { direction: direction }
        });
    }, [direction, dispatch]);
    return (React.createElement(RovingTabIndexContext.Provider, { value: context }, children));
};

var TabDirection;
(function (TabDirection) {
    TabDirection[TabDirection["Next"] = 0] = "Next";
    TabDirection[TabDirection["Previous"] = 1] = "Previous";
})(TabDirection || (TabDirection = {}));
// domElementRef:
//   - a React DOM element ref of the DOM element that is the focus target
//   - this must be the same DOM element for the lifecycle of the component
// disabled:
//   - can be updated as appropriate to reflect the current enabled or disabled
//     state of the component
// id:
//   - an optional ID that is the unique ID for the component
//   - if provided then it must be a non-empty string
//   - if not provided then one will be autogenerated
// The returned callbacks handleKeyDown and handleClick are stable.
function useRovingTabIndex(
// eslint-disable-next-line @typescript-eslint/no-explicit-any
domElementRef, disabled, id) {
    // This id is stable for the life of the component:
    var tabIndexId = React.useRef(id || uniqueId("roving-tabindex_"));
    var context = React.useContext(RovingTabIndexContext);
    // Registering and unregistering are tied to whether the input is disabled or not.
    // Context is not in the inputs because context.dispatch is stable.
    React.useLayoutEffect(function () {
        if (disabled) {
            return;
        }
        context.dispatch({
            type: ActionTypes.REGISTER,
            payload: { id: tabIndexId.current, domElementRef: domElementRef }
        });
        return function () {
            context.dispatch({
                type: ActionTypes.UNREGISTER,
                payload: { id: tabIndexId.current }
            });
        };
    }, [disabled]);
    var getDirection = function (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event) {
        if (context.state.direction === "horizontal" ||
            context.state.direction === "both") {
            if (event.key === "ArrowLeft") {
                return TabDirection.Previous;
            }
            else if (event.key === "ArrowRight") {
                return TabDirection.Next;
            }
        }
        if (context.state.direction === "vertical" ||
            context.state.direction === "both") {
            if (event.key === "ArrowUp") {
                return TabDirection.Previous;
            }
            else if (event.key === "ArrowDown") {
                return TabDirection.Next;
            }
        }
        return null;
    };
    var handleKeyDown = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function (event) {
        var payload = { id: tabIndexId.current };
        var direction = getDirection(event);
        if (direction === TabDirection.Previous) {
            context.dispatch({
                type: ActionTypes.TAB_TO_PREVIOUS,
                payload: payload
            });
            event.preventDefault();
        }
        else if (direction === TabDirection.Next) {
            context.dispatch({
                type: ActionTypes.TAB_TO_NEXT,
                payload: payload
            });
            event.preventDefault();
        }
        else if (event.key === "Home") {
            context.dispatch({ type: ActionTypes.TAB_TO_FIRST });
            event.preventDefault();
        }
        else if (event.key === "End") {
            context.dispatch({ type: ActionTypes.TAB_TO_LAST });
            event.preventDefault();
        }
    }, [context.state]);
    var handleClick = React.useCallback(function () {
        context.dispatch({
            type: ActionTypes.CLICKED,
            payload: { id: tabIndexId.current }
        });
    }, []);
    var selected = !disabled && tabIndexId.current === context.state.selectedId;
    var tabIndex = selected ? 0 : -1;
    var focused = selected && context.state.lastActionOrigin !== null;
    return [tabIndex, focused, handleKeyDown, handleClick];
}

// Invokes focus() on ref as a layout effect whenever focused
// changes from false to true.
function useFocusEffect(focused, 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
ref) {
    React.useLayoutEffect(function () {
        if (focused && ref.current) {
            ref.current.focus();
        }
    }, [focused]);
}

export { Provider as RovingTabIndexProvider, useFocusEffect, useRovingTabIndex };
//# sourceMappingURL=index.es.js.map
